import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import {
    changeFieldProperty,
    changeFormField,
    changeFormFieldValue,
    setFieldError,
    setNewFields,
    setNewForm,
    updateFormProperties,
} from 'apps/auth/actions/formAction';
import { changePhoneCountryCode } from 'apps/auth/actions/loginAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { sendGtEvent } from 'apps/auth/analytics/analytics';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import PhoneSuggestionPopup from 'apps/auth/components/common/PhoneSuggestionPopup/PhoneSuggestionPopup';
import SelectCountryWithCodeScreen from 'apps/auth/components/common/search/SelectCountryWithCodeScreen';
import InputPhoneWithDropdown from 'apps/auth/components/form/InputPhoneWithDropdown';
import Form from 'apps/auth/components/form-multi/Form';
import { clearErrorField, shouldValidateNumberFormat } from 'apps/auth/components/form-multi/utils';
import { getFullCountryName } from 'apps/auth/components/utils/AmplitudeUtils';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import {
    COUNTRY_CODE_SELECTED,
    LOGIN_OPENED,
    MOBILE_NUMBER_CONTINUE_CLICKED,
    MOBILE_NUMBER_ERROR_SHOWN,
    MOBILE_NUMBER_SCREEN_SHOWN,
    OPT_IN_BETA_MIGRATION_SCREEN_SHOWN,
    REGISTRATION_STARTED,
    USE_THIS_PHONE_NUMBER_CLICKED,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { COUNTRY_OF_RESIDENCE, FLOW, NEW_PHONE_COUNTRY, PHONE_COUNTRY, PHONE_NUMBER } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { LOGIN_AMPLITUDE, SIGNUP_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { J_CAPTCHA, J_PASSWORD, J_USERNAME } from 'apps/auth/constants/Constants';
import { AUTH_TYPE } from 'apps/auth/constants/ExtraNames';
import { BIRTHDAY, PHONE } from 'apps/auth/constants/fieldsName';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { GT_REG_PHONE_DONE } from 'apps/auth/constants/GoogleAnalyticsEvents';
import { AUTH, LOGIN } from 'apps/auth/constants/UrlConstants';
import { UTM_PARAM_NAMES } from 'apps/auth/constants/utmParams';
import { addScript, removeScript } from 'apps/auth/helpers/DOM';
import { toUpperCase } from 'apps/auth/helpers/utils';
import { getUtmParamsFromSearch } from 'apps/auth/helpers/utmParams';
import { usePageMessages } from 'apps/auth/hooks/selectors';
import { useFlags } from 'apps/auth/hooks/useFlags';
import { useQuery } from 'apps/auth/hooks/useQuery';
import Cookies from 'universal-cookie';

import { HAS_PASSWORD, NEW_USER } from './constants';
import DownloadAppPopup from './DownloadAppPopup';
import PhoneInputErrorPopup from './PhoneInputErrorPopup';

const InsertPhone = ({ fromPage = SEND_FORM_NAME, ...props }) => {
    const submitRef = useRef(null);
    const userInputRef = useRef('');

    const [iso2, updateIso2] = useState(() => {
        if (__isBrowser__) {
            const saved = localStorage.getItem('prevLoginIso2');
            return props.loginCountryCode || saved || props.defaultCountry;
        }
        return props.loginCountryCode || props.defaultCountry;
    });
    const [prefix, setPrefix] = useState('');
    const [userInputWithoutPrefix, setUserInputWithoutPrefix] = useState('');
    const [countryName, setCountryName] = useState('');
    const [focusOnLogin, setFocusOnLogin] = useState(true);
    const [hasValidationData, setValidationData] = useState(false);
    const [isLoginInputError, setLoginInputError] = useState(false);
    const [flowName, setFlowName] = useState('');
    const [invitedUserCountryCode, setInvitedUserCountryCode] = useState(null);
    const [hydrated, setHydrated] = useState(false);
    const currentPhoneCountryCode = props.phoneCountry?.code;
    const messages = usePageMessages();
    const query = useQuery();
    const { phone_number_suggestion_at_the_registration_countries_list: countriesToValidate } = useFlags();
    const countryId = props.countries?.find(({ code }) => code === iso2)?.id;

    useEffect(() => setHydrated(true), []);

    useEffect(() => {
        if (countryId != null && countryId !== '') {
            props.changeFormFieldValue(props.formName, countryId, 'countryId');
        }
    }, [countryId, props.formName]);

    useEffect(() => {
        getUtmParams().forEach(([key, value]) => {
            props.changeFormFieldValue(props.formName, value, key);
        });
    }, []);

    useEffect(() => {
        // prevent users from entering auth zone after logout
        const handlePopState = () => {
            const currentPath = window.location.pathname;
            if (currentPath.startsWith(AUTH)) {
                window.history.pushState(null, '', LOGIN);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        const saved = __isBrowser__ ? localStorage.getItem('prevLoginIso2') : null;
        if (!saved) {
            const next = props.loginCountryCode || props.defaultCountry;
            if (next && next !== iso2) updateIso2(next);
        }
    }, [props.loginCountryCode, props.defaultCountry]);

    useEffect(() => {
        let currentCountry;

        const invitedUser = JSON.parse(sessionStorage?.getItem('invitedUserData'));
        if (invitedUser && Object.keys(invitedUser).length > 0) {
            currentCountry = props.countries.find(({ code }) => code === invitedUser.country);
            updateIso2(currentCountry.code);
            setInvitedUserCountryCode(currentCountry.code);
            props.changeFormFieldValue(props.formName, invitedUser.phone, PHONE);
            sessionStorage.removeItem('invitedUserData');
        } else {
            currentCountry = props.countries.find(({ code }) => code === iso2);
        }

        const { name: currentName, prefix: currentPrefix } = currentCountry;
        setCountryName(currentName);
        setPrefix(currentPrefix);
        if (__isBrowser__) {
            let param = '';
            const currentUrl = window.location.pathname;
            if (currentUrl.endsWith(LOGIN)) {
                param = LOGIN_AMPLITUDE;
                setFlowName(LOGIN_AMPLITUDE);
            } else if (currentUrl.endsWith('/registration')) {
                param = SIGNUP_AMPLITUDE;
                setFlowName(SIGNUP_AMPLITUDE);
            }
            amplitude.getInstance().logEvent(MOBILE_NUMBER_SCREEN_SHOWN, {
                [FLOW]: param,
                [PHONE_COUNTRY]: currentName,
            });
        }

        // libphonenumber library
        const libphonenumberFile = '/libphonenumber-mobile.js?v=1.10.60';
        addScript(libphonenumberFile, () => setValidationData(true));
        return () => removeScript(libphonenumberFile);
    }, []);

    useEffect(() => {
        const value = props.fields?.birthday?.value;
        if (value) {
            const numberValue = value.replace(/[^0-9]+/g, '');
            if (numberValue?.length < 8) {
                props.changeFieldProperty(props.formName, BIRTHDAY, 'value', null);
            }
        }
    }, [props.fields?.birthday?.value]);

    useEffect(() => {
        props.changeFieldProperty(props.formName, 'phone', 'comment', messages[iso2 + 'CountryComment']);
        if (!props.loginCountryCode && !invitedUserCountryCode) {
            props.changePhoneCountryCode(iso2);
        }
        if (invitedUserCountryCode) {
            props.changePhoneCountryCode(invitedUserCountryCode);
        }
    }, [invitedUserCountryCode, props.defaultCountry, iso2, props.loginCountryCode]);

    const amplitudeEventOnClick = (phoneNumber = props.fieldPhone.value) => {
        amplitude.getInstance().setUserId(phoneNumber);
        amplitude.getInstance().setUserProperties({ [COUNTRY_OF_RESIDENCE]: getFullCountryName(iso2, props.allCountries) });
        amplitude.getInstance().logEvent(MOBILE_NUMBER_CONTINUE_CLICKED, {
            [PHONE_COUNTRY]: getFullCountryName(iso2, props.allCountries),
            [FLOW]: flowName,
        });
    };

    const sendStatEvent = (json) => {
        const authType = json.extra?.[AUTH_TYPE];
        const simpleRegistration = json.extra?.simpleRegistration;
        if (authType === NEW_USER) {
            amplitude.getInstance().setUserProperties({ simplified_onboarding: simpleRegistration ? '1' : '0' });
            amplitude.getInstance().logEvent(REGISTRATION_STARTED);
            sendGtEvent(GT_REG_PHONE_DONE);
        } else {
            amplitude.getInstance().logEvent(LOGIN_OPENED);
        }
    };

    const validateNumberFormat = (number, countryCode) => {
        const numberToValidate = `+${number}`;
        const hasLibphonenumber = hasValidationData || typeof libphonenumber !== 'undefined';
        if (hasLibphonenumber) {
            const phoneNumber = libphonenumber.parsePhoneNumber(numberToValidate, countryCode);
            if (phoneNumber?.isValid()) {
                const formattedNumber = `${phoneNumber.countryCallingCode}${phoneNumber.nationalNumber}`;
                if (number !== formattedNumber) {
                    props.changeFormFieldValue(props.formName, formattedNumber, PHONE).then(() => {
                        submitNumberAndLogEvent(formattedNumber);
                    });
                } else {
                    submitNumberAndLogEvent();
                }
            } else {
                amplitude.getInstance().logEvent(MOBILE_NUMBER_ERROR_SHOWN, { [PHONE_NUMBER]: phoneNumber?.number });
                openErrorPopup();
                setLoginInputError(true);
                props.setFieldError(props.formName, PHONE, messages.cantRecogniseNumber);
            }
        } else {
            submitNumberAndLogEvent();
        }
    };
    // BE validation switched off
    // eslint-disable-next-line no-unused-vars
    const validateAndSubmit = () => {
        if (shouldValidateUserNumberFormat()) {
            validateNumberFormat(props?.fieldPhone?.value, toUpperCase(iso2));
        } else {
            submitNumberAndLogEvent();
        }
    };

    const shouldValidateUserNumberFormat = () => {
        return shouldValidateNumberFormat(iso2, countriesToValidate, props.countries);
    };

    const submitHandler = (_fields, json) => {
        sendStatEvent(json);
        if (json?.extra?.webUzbekistanUsersDisabled) {
            showUzbekistanUsersDisabledPopup();
            return;
        } else if (json?.extra?.showPreparedPs3MigrationScreen) {
            showPs3BetaModal(json.messages);
        } else if (json?.extra?.showSuggestion && json?.extra?.phoneSuggestion) {
            const enteredValue = props.fieldPhone?.value;
            const suggestedValue = json.extra.phoneSuggestion;
            const { popUpPhoneSuggestionInfo, useCorrectedNumber } = json?.messages ?? {};
            openPhoneSuggestionPopup(enteredValue, suggestedValue, popUpPhoneSuggestionInfo, useCorrectedNumber);
            return;
        } else if (json && json.forms && !json.extra?.smsConfirmation) {
            const form = json.forms[props.formName];
            const fields = form?.fields;
            if (fields && Object.keys(fields).length > 0) {
                if (json.extra[AUTH_TYPE] === HAS_PASSWORD) {
                    fields.password.name = J_PASSWORD;
                    fields.phone.name = J_USERNAME;
                    fields.captcha.name = J_CAPTCHA;
                }
                if (!json.errors.phone && !json.extra.errorCode) {
                    if (currentPhoneCountryCode === json.extra?.phoneCountry?.code) {
                        props.setNewFields(props.formName, fields);
                    } else {
                        props.setNewForm(props.formName, fields, form.action);
                    }
                }
            }
            if (form && form.action) {
                props.updateFormProperties(props.formName, form.action);
            }
        }
        if (json && json.forms && json.extra?.smsConfirmation) {
            const form = json.forms[props.formName];
            const fields = form?.fields;
            if (fields && Object.keys(fields).length > 0) {
                props.setNewFields(props.formName, fields);
            }
        }
    };

    // Url params come from the landing page links and win over the cookies
    // written on the landing page itself, which stay as a fallback.
    const getUtmParams = () => {
        const cookies = new Cookies();
        const utmParamsFromUrl = getUtmParamsFromSearch(query);
        return UTM_PARAM_NAMES.map((name) => [name, utmParamsFromUrl.get(name) || cookies.get(name)]).filter(([, value]) => Boolean(value));
    };

    const submitNumberAndLogEvent = (phoneNumber) => {
        amplitudeEventOnClick(phoneNumber);
        submitRef.current?.click();
    };

    const showCountryModal = () => {
        props.toggleModal(
            true,
            <SelectCountryWithCodeScreen
                countryWithCode={true}
                iso2={iso2}
                onCountryItemClick={onCountryItemClick}
                closeModal={closeModal}
                formName={props.formName}
                countryName={countryName}
            />,
        );
    };

    const showPs3BetaModal = (messages = {}) => {
        amplitude.getInstance().logEvent(OPT_IN_BETA_MIGRATION_SCREEN_SHOWN);
        props.toggleModal(
            true,
            <ModalComponent isVerticalCentered canCloseModal={false}>
                <InfoPage
                    insideModalComponent
                    iconName="clock-80"
                    iconColor="primary"
                    primaryButtonCaption={messages?.gotIt}
                    headerText={messages?.preparingBetaTitle}
                    secondaryText={<DangerouslyHtml dangerouslyHtml={messages?.preparingBetaSubtitle} />}
                    primaryButtonOnClick={(e) => {
                        e.preventDefault();
                        props.toggleModal(false);
                    }}
                />
            </ModalComponent>,
        );
    };
    const closeModal = () => {
        props.toggleModal(false);
        setFocusOnLogin(false);
    };
    const onCountryItemClick = (code, prefix, newCountryName) => {
        updateIso2(code);
        setPrefix(prefix);
        setInvitedUserCountryCode(null);
        props.toggleModal(false);
        props.changePhoneCountryCode(code);
        props.changeFieldProperty(props.formName, 'phone', 'comment', messages[code + 'CountryComment']);
        amplitude.getInstance().logEvent(COUNTRY_CODE_SELECTED, {
            [COUNTRY_OF_RESIDENCE]: getFullCountryName(code, props.allCountries),
            [PHONE_COUNTRY]: newCountryName,
            [NEW_PHONE_COUNTRY]: getFullCountryName(code, props.allCountries),
        });
        setCountryName(newCountryName);

        const focus = code !== iso2;
        setFocusOnLogin(focus);
        if (focus) {
            clearErrorField(props.formName, props.fieldPhone, props.changeFormField);
        }
    };

    const getUserInputWithoutPrefix = (value) => {
        setUserInputWithoutPrefix(value);
    };

    const openErrorPopup = () => {
        const { phoneNotSupported1, phoneNotSupported4, changeCountryButton, reenterManuallyButton } = messages;
        props.popupRef.current?.open(
            <PhoneInputErrorPopup
                countryName={countryName}
                prefix={prefix}
                userInputWithoutPrefix={userInputWithoutPrefix}
                formattedNumber={props.formattedNumber}
                showCountryModal={showCountryModal}
                popupRef={props.popupRef}
                text1={phoneNotSupported1}
                text2={phoneNotSupported4}
                changeCountryButton={changeCountryButton}
                reenterManuallyButton={reenterManuallyButton}
            />,
        );
    };

    const showUzbekistanUsersDisabledPopup = () => {
        props.popupRef.current?.open(<DownloadAppPopup popupRef={props.popupRef} onClick={() => props.popupRef.current?.close()} />);
    };

    const openPhoneSuggestionPopup = (enteredValue, suggestedValue, popUpPhoneSuggestionInfo, useCorrectedNumber) => {
        props.popupRef.current?.open(
            <PhoneSuggestionPopup
                popupRef={props.popupRef}
                text={popUpPhoneSuggestionInfo}
                enteredNumber={enteredValue}
                suggestedNumber={suggestedValue}
                countryCode={iso2}
                buttonText={useCorrectedNumber}
                onConfirm={() => {
                    props.changeFormFieldValue(props.formName, suggestedValue, PHONE).then(() => {
                        amplitude.getInstance().setUserId(suggestedValue);
                        amplitude.getInstance().logEvent(USE_THIS_PHONE_NUMBER_CLICKED);
                        submitRef.current?.click();
                    });
                }}
            />,
        );
    };

    const disabled = !(props?.fieldPhone?.value && props?.fieldPhone?.value.length >= 6 && !props?.fieldPhone?.isError);

    return (
        <>
            <HeaderPanel title={props.headerTitle} />
            <Form formName={props.formName} onSubmitHandler={submitHandler} action={`/check-phone.action?from_page=${fromPage}`} checkNotVisibleField={false}>
                <fieldset>
                    {hydrated && (
                        <InputPhoneWithDropdown
                            field={props.fieldPhone}
                            submitRef={submitRef}
                            submitNumberOnLogin={submitNumberAndLogEvent}
                            defaultCountry={props.defaultCountry}
                            focus={focusOnLogin}
                            submitByEnter={true}
                            formName={props.formName}
                            iso2={iso2}
                            userInputRef={userInputRef}
                            changeFormFieldValue={props.changeFormFieldValue}
                            prefix={prefix}
                            getUserInputWithoutPrefix={getUserInputWithoutPrefix}
                            userInputWithoutPrefix={userInputWithoutPrefix}
                            showCountryModal={showCountryModal}
                            hintButtonClick={openErrorPopup}
                            isLoginInputError={isLoginInputError}
                            setLoginInputError={setLoginInputError}
                            setFocusOnLogin={setFocusOnLogin}
                            countryName={countryName}
                            flowName={flowName}
                            setFormattedNumber={props.setFormattedNumber}
                        />
                    )}
                </fieldset>
                <FixedBottomPanel isRightAligned buttonPosition={props.buttonPosition}>
                    <Button
                        type="button"
                        raised={true}
                        size="fit"
                        caption={props.button}
                        disabled={disabled}
                        dataTestId="insert-phone-btn-login"
                        onClick={() => {
                            submitNumberAndLogEvent();
                        }}
                    />
                    <Button type="submit" variant={'hidden'} ref={submitRef} />
                </FixedBottomPanel>
            </Form>
        </>
    );
};

const mapStateToProps = ({ app, form, messages, extra, login }, ownProps) => ({
    country: app.country,
    fields: form[ownProps.formName].fields,
    fieldPhone: form[ownProps.formName].fields.phone,
    button: messages.pageMessages.button,
    defaultCountry: extra.defaultCountry,
    allCountries: extra.allCountries,
    countries: extra.countries,
    phoneCountry: extra.phoneCountry,
    errorCode: extra.errorCode,
    headerTitle: messages.pageMessages?.insertPhoneHeaderTitle,
    loginCountryCode: login.countryCode,
});

export default connect(mapStateToProps, {
    setNewFields,
    updatePageMessages,
    setNewForm,
    setFieldError,
    updateFormProperties,
    changeFieldProperty,
    changeFormField,
    changeFormFieldValue,
    changePhoneCountryCode,
    toggleModal,
})(InsertPhone);
